"use client";

import React, { useState } from "react";
import Link from "next/link";
import { X, FileText, ArrowRight, Download, CheckCircle2, ShieldCheck } from "lucide-react";
import confetti from "canvas-confetti";
import { PropertyItem, FEATURED_PROPERTIES } from "@/lib/demos/propertiesData";

interface BrochureDownloadModalProps {
  property?: PropertyItem | null;
  currency?: "INR" | "USD";
  onClose: () => void;
  onLeadCreated?: () => void;
}

export default function BrochureDownloadModal({
  property,
  currency = "INR",
  onClose,
  onLeadCreated,
}: BrochureDownloadModalProps) {
  const [selectedPropertyId, setSelectedPropertyId] = useState(property?.id || FEATURED_PROPERTIES[0].id);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [investorType, setInvestorType] = useState("Investor");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloaded, setIsDownloaded] = useState(false);

  const selectedProp = FEATURED_PROPERTIES.find((p) => p.id === selectedPropertyId) || FEATURED_PROPERTIES[0];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const budgetFormatted = currency === "INR" ? selectedProp.priceINR : selectedProp.priceUSD;
      const budgetNum = currency === "INR" ? selectedProp.numericINR : selectedProp.numericUSD;

      const payload = {
        name,
        email,
        phone,
        property_name: selectedProp.title,
        property_type: selectedProp.type,
        budget_range: budgetFormatted,
        budget_numeric: budgetNum,
        currency,
        buyer_type: investorType,
        lead_source: "Brochure Download Gate",
        notes: `Downloaded complete floor plan & investment dossier for ${selectedProp.title}. Automated WhatsApp follow-up scheduled.`,
      };

      const res = await fetch("/api/demos/real-estate/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setIsDownloaded(true);
        if (onLeadCreated) onLeadCreated();

        confetti({
          particleCount: 100,
          spread: 60,
          origin: { y: 0.6 },
        });
      }
    } catch (err) {
      console.error("Error downloading brochure:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-fadeIn">
      <div className="relative w-full max-w-lg bg-slate-900 border border-amber-500/30 rounded-2xl shadow-2xl overflow-hidden text-slate-100 flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 bg-gradient-to-r from-slate-900 to-amber-950/30 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-amber-400">
                Institutional Dossier
              </span>
              <h2 className="text-base font-bold text-white font-heading">
                Download Floor Plans & Yield Projections
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex-grow">
          {isDownloaded ? (
            <div className="text-center py-6 px-2 flex flex-col items-center">
              <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-3 text-emerald-400 animate-bounce">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-white mb-1">
                Dossier Ready for Download
              </h3>
              <p className="text-xs text-slate-300 max-w-sm mb-5 leading-relaxed">
                The technical prospectus and price-to-yield schedule for{" "}
                <strong className="text-amber-400">{selectedProp.title}</strong> has been prepared.
              </p>

              <div className="w-full p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-left mb-5">
                <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wider block mb-0.5">
                  CRM Workflow Triggered
                </span>
                <p className="text-xs text-slate-300">
                  A new prospect card has been created in the <strong>New Inquiries</strong> column of the Real Estate CRM!
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-3 w-full justify-center">
                <a
                  href="#download"
                  onClick={(e) => {
                    e.preventDefault();
                    alert("Demo: In a live system, this instantly triggers the official PDF brochure download!");
                  }}
                  className="w-full sm:w-auto px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-semibold text-xs flex items-center justify-center gap-2 border border-slate-700"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Download PDF (14 MB)</span>
                </a>

                <Link
                  href="/demos/real-estate/crm"
                  className="w-full sm:w-auto px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-bold text-xs shadow-md flex items-center justify-center gap-1.5"
                >
                  <span>View in Sales CRM</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3.5">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Selected Development
                </label>
                <select
                  value={selectedPropertyId}
                  onChange={(e) => setSelectedPropertyId(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-xs focus:border-amber-500 focus:outline-none"
                >
                  {FEATURED_PROPERTIES.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title} ({p.city}) — {currency === "INR" ? p.priceINR : p.priceUSD}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Your Full Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Gautam Singhania"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-xs focus:border-amber-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Corporate / Personal Email *
                </label>
                <input
                  type="email"
                  required
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-xs focus:border-amber-500 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    WhatsApp Number *
                  </label>
                  <input
                    type="tel"
                    required
                    placeholder="+91 98200..."
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-xs focus:border-amber-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    Investment Profile
                  </label>
                  <select
                    value={investorType}
                    onChange={(e) => setInvestorType(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-xs focus:border-amber-500 focus:outline-none"
                  >
                    <option value="Investor">Institutional Investor</option>
                    <option value="End-User">Family Primary Residence</option>
                    <option value="NRI">NRI / Overseas Portfolio</option>
                  </select>
                </div>
              </div>

              <div className="pt-2 flex items-center justify-between">
                <div className="flex items-center gap-1 text-[10px] text-slate-400">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Immediate PDF dispatch via Email & WhatsApp</span>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-5 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-bold text-xs shadow-md hover:shadow-amber-500/20 transition-all hover:scale-[1.02] disabled:opacity-50"
                >
                  {isSubmitting ? "Generating Dossier..." : "Get Dossier"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
